import { Badge, Button, Card, Tabs, TabsContent } from '@/components/ui'
import {
  Building2,
  Maximize2,
  Minimize2,
  Moon,
  RefreshCw,
  Search,
  Sun,
  Tv,
  UserRound,
  Wifi,
  WifiOff,
  X,
} from 'lucide-react'
import type {
  WallboardAttendanceStatus,
  WallboardBoard,
  WallboardCompanySummary,
  WallboardMemberRow,
  WallboardPresenceEvent,
  WallboardPresenceStatus,
  WallboardQuery,
} from '@/features/wallboard/types/wallboard.types'
import {
  buildWallboardSearchParams,
  normalizeWallboardQuery,
  parseWallboardSearchParams,
} from '@/features/wallboard/utils/wallboardQuery'
import { differenceInMinutes, parseISO } from 'date-fns'
import { getCompanyColor, hexToRgba } from '@/features/shifts/utils/companyColor'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useWallboard, wallboardQueryKeys } from '@/features/wallboard/hooks/useWallboard'

import type { BadgeVariant } from '@/components/ui'
import { CompanyChips } from '@/features/shifts/components/CompanyChips'
import { EmptyState } from '@/components/common/EmptyState'
import { PageShell } from '@/components/common/PageShell'
import { cn } from '@/lib/cn'
import { formatDateTime } from '@/lib/formatters'
import { useCompanyNavList } from '@/features/companies/hooks/useCompanyNavList'
import { useFullscreenShortcut } from '@/hooks/useFullscreenShortcut'
import { useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { useSocket } from '@/providers/SocketProvider'
import { QueryErrorState } from '@/components/common/QueryErrorState'

type WallboardView = 'company' | 'person'
/** The single status a person's dot encodes on a company card. */
type MemberStatus = 'no_show' | 'due_soon' | 'online' | 'break' | 'idle' | 'offline'
/** `clocked_in` is attendance, not a dot colour: it overlaps every presence. */
type PersonFilter = 'all' | MemberStatus | 'clocked_in'
type CardAppearance = 'light' | 'dark'

const CARD_APPEARANCE_KEY = 'wallboard-card-appearance'

const PRESENCE_STATUS_LABEL: Record<WallboardPresenceStatus, string> = {
  online: 'Online',
  idle: 'Idle',
  offline: 'Offline',
}

// The dot legend: one colour per person status, shared by the company card
// rows and the legend in the command bar so the board reads the same way in
// both places. `break` wins over presence because "clocked in but away" is the
// more actionable fact than "socket still connected".
const MEMBER_STATUS_ORDER: MemberStatus[] = [
  'no_show',
  'due_soon',
  'online',
  'break',
  'idle',
  'offline',
]

const MEMBER_STATUS_PRIORITY: Record<MemberStatus, number> = {
  no_show: 0,
  due_soon: 1,
  online: 2,
  break: 3,
  idle: 4,
  offline: 5,
}

const MEMBER_STATUS_META: Record<
  MemberStatus,
  {
    label: string
    caption: string
    hint: string
    dot: string
    dotOnDark: string
    tint: string
    /** Tile when this status is the active filter. */
    selected: string
  }
> = {
  no_show: {
    label: 'No show',
    caption: 'missed a started shift',
    hint: 'Shift started and they never clocked in',
    dot: 'bg-rose-500',
    dotOnDark: 'bg-rose-400',
    tint: 'text-rose-700 dark:text-rose-300',
    selected: 'border-rose-300 bg-rose-50 ring-2 ring-rose-200 dark:border-rose-500/50 dark:bg-rose-500/10 dark:ring-rose-500/20',
  },
  due_soon: {
    label: 'Due soon',
    caption: 'shift starting, not here',
    hint: 'Shift starts shortly and they have not clocked in',
    dot: 'bg-violet-500',
    dotOnDark: 'bg-violet-400',
    tint: 'text-violet-700 dark:text-violet-300',
    selected: 'border-violet-300 bg-violet-50 ring-2 ring-violet-200 dark:border-violet-500/50 dark:bg-violet-500/10 dark:ring-violet-500/20',
  },
  online: {
    label: 'Online',
    caption: 'connected & active',
    hint: 'Connected and active',
    dot: 'bg-emerald-500',
    dotOnDark: 'bg-emerald-300',
    tint: 'text-emerald-700 dark:text-emerald-300',
    selected: 'border-emerald-300 bg-emerald-50 ring-2 ring-emerald-200 dark:border-emerald-500/50 dark:bg-emerald-500/10 dark:ring-emerald-500/20',
  },
  break: {
    label: 'On break',
    caption: 'clocked in, away',
    hint: 'Clocked in, currently on break',
    dot: 'bg-sky-500',
    dotOnDark: 'bg-sky-300',
    tint: 'text-sky-700 dark:text-sky-300',
    selected: 'border-sky-300 bg-sky-50 ring-2 ring-sky-200 dark:border-sky-500/50 dark:bg-sky-500/10 dark:ring-sky-500/20',
  },
  idle: {
    label: 'Idle',
    caption: 'no recent activity',
    hint: 'Connected, no activity recently',
    dot: 'bg-amber-500',
    dotOnDark: 'bg-amber-300',
    tint: 'text-amber-700 dark:text-amber-300',
    selected: 'border-amber-300 bg-amber-50 ring-2 ring-amber-200 dark:border-amber-500/50 dark:bg-amber-500/10 dark:ring-amber-500/20',
  },
  offline: {
    label: 'Offline',
    caption: 'not connected',
    hint: 'Not connected or logged out',
    dot: 'bg-gray-400 dark:bg-gray-500',
    dotOnDark: 'bg-white/45',
    tint: 'text-gray-600 dark:text-gray-300',
    selected: 'border-gray-300 bg-gray-50 ring-2 ring-gray-200 dark:border-gray-500 dark:bg-gray-700/40 dark:ring-gray-600/40',
  },
}

type MemberStatusCounts = Record<MemberStatus, number>

// Exact per-dot counts over distinct people. A capped board omits offline rows
// from the payload (and only those), so the shortfall against the headline
// member total is offline by definition — folding it back in keeps the four
// chips summing to the roster.
function countMemberStatuses(
  people: WallboardMemberRow[],
  memberTotal: number,
): MemberStatusCounts {
  const counts: MemberStatusCounts = {
    no_show: 0,
    due_soon: 0,
    online: 0,
    break: 0,
    idle: 0,
    offline: 0,
  }
  for (const row of people) counts[getMemberStatus(row)] += 1
  counts.offline += Math.max(0, memberTotal - people.length)
  return counts
}

// A no-show outranks presence: someone logged in from home during a shift they
// never clocked into is still a no-show. The attendance re-check means a live
// clock-in over the socket clears the dot without waiting for a refetch.
function isClockedIn(row: Pick<WallboardMemberRow, 'attendanceStatus'>): boolean {
  return row.attendanceStatus === 'clocked_in' || row.attendanceStatus === 'on_break'
}

function getMemberStatus(row: WallboardMemberRow): MemberStatus {
  // Clocked out of an earlier shift doesn't excuse a later one.
  const clockedIn = isClockedIn(row)
  if (row.noShow && !clockedIn) return 'no_show'
  // Coming online before the shift starts is the good outcome, so it clears the
  // warning immediately rather than waiting for the next refetch.
  if (row.dueSoon && !clockedIn && row.presenceStatus !== 'online') {
    return 'due_soon'
  }
  if (row.attendanceStatus === 'on_break') return 'break'
  return row.presenceStatus
}

const ATTENDANCE_STATUS_LABEL: Record<WallboardAttendanceStatus, string> = {
  not_clocked_in: 'Not clocked in',
  clocked_in: 'Clocked in',
  on_break: 'On break',
  clocked_out: 'Clocked out',
}

const PRESENCE_BADGE_VARIANT: Record<WallboardPresenceStatus, BadgeVariant> = {
  online: 'success',
  idle: 'warning',
  offline: 'neutral',
}

const ATTENDANCE_BADGE_VARIANT: Record<WallboardAttendanceStatus, BadgeVariant> = {
  not_clocked_in: 'neutral',
  clocked_in: 'success',
  on_break: 'warning',
  clocked_out: 'neutral',
}

// Driven off the dot statuses so the tab's filters, its counts and the card
// dots can never drift apart.
// "Clocked in" is attendance: the same number the dashboard shows, and it
// overlaps the presence dots (clocked in can be online, idle or not connected).
const CLOCKED_IN_META = {
  label: 'Clocked in',
  hint: 'Clocked in or on break right now (attendance, same as the dashboard)',
  dot: 'bg-primary',
}

function filterLabel(filter: Exclude<PersonFilter, 'all'>): string {
  return filter === 'clocked_in' ? CLOCKED_IN_META.label : MEMBER_STATUS_META[filter].label
}

function filterDot(filter: Exclude<PersonFilter, 'all'>): string {
  return filter === 'clocked_in' ? CLOCKED_IN_META.dot : MEMBER_STATUS_META[filter].dot
}

const PERSON_FILTERS: Array<{ value: PersonFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'clocked_in', label: CLOCKED_IN_META.label },
  ...MEMBER_STATUS_ORDER.map((status) => ({
    value: status as PersonFilter,
    label: MEMBER_STATUS_META[status].label,
  })),
]

function formatMaybeTime(value: string | null): string {
  return value ? formatDateTime(value, 'h:mm a') : '-'
}

function formatElapsedSince(value: string | null, serverTime: string | undefined): string {
  if (!value) return '-'
  const minutes = Math.max(0, differenceInMinutes(serverTime ? parseISO(serverTime) : new Date(), parseISO(value)))
  if (minutes < 1) return 'now'
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  if (hours < 24) return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`
  const days = Math.floor(hours / 24)
  return `${days}d`
}

/** Time from `serverTime` forward to `value` — the mirror of the elapsed helper. */
function formatElapsedUntil(value: string | null, serverTime: string | undefined): string {
  if (!value) return '-'
  const minutes = Math.max(
    0,
    differenceInMinutes(parseISO(value), serverTime ? parseISO(serverTime) : new Date()),
  )
  if (minutes < 1) return 'moments'
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`
}

function getConnectionBadge(isConnected: boolean): {
  label: string
  variant: BadgeVariant
  icon: typeof Wifi
} {
  return isConnected
    ? { label: 'Live', variant: 'success', icon: Wifi }
    : { label: 'Offline', variant: 'warning', icon: WifiOff }
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('')
}

// "Online" here is the same dot status the card header counts.
function countOnlineDots(company: WallboardCompanySummary): number {
  return company.members.filter((row) => getMemberStatus(row) === 'online').length
}

function getCompanyAttentionScore(company: WallboardCompanySummary): number {
  const noOnlinePenalty =
    company.totalMembers > 0 && countOnlineDots(company) === 0 ? 100 : 0
  return (
    noOnlinePenalty +
    company.noShow * 40 +
    company.dueSoon * 12 +
    company.idle * 6 +
    company.onBreak * 3
  )
}

// Card roster order: most present first (online, on break, idle, offline),
// then alphabetical, so the people who matter sit at the top of the list.
function sortCompanyRoster(rows: WallboardMemberRow[]): WallboardMemberRow[] {
  return [...rows].sort((a, b) => {
    const diff = MEMBER_STATUS_PRIORITY[getMemberStatus(a)] - MEMBER_STATUS_PRIORITY[getMemberStatus(b)]
    if (diff !== 0) return diff
    return a.user.fullName.localeCompare(b.user.fullName)
  })
}

// Offline rows are dropped from the payload when the board is capped, so the
// list can be shorter than the roster; the counts on the summary stay exact.
function getHiddenMemberCount(company: WallboardCompanySummary): number {
  return Math.max(0, company.totalMembers - company.members.length)
}

// The short line a member row shows on its right: why this person is in this
// state, not just when they were last seen.
function getMemberDetail(
  row: WallboardMemberRow,
  status: MemberStatus,
  serverTime: string | undefined,
): string {
  if (status === 'no_show') {
    const due = formatMaybeTime(row.shiftStartAt)
    const late = formatElapsedSince(row.shiftStartAt, serverTime)
    return row.shiftStartAt ? `Due ${due} · ${late} late` : 'Missed shift'
  }
  if (status === 'due_soon') {
    return row.shiftStartAt
      ? `Starts ${formatMaybeTime(row.shiftStartAt)} · in ${formatElapsedUntil(row.shiftStartAt, serverTime)}`
      : 'Shift starting'
  }
  if (status === 'idle') return `Idle ${formatElapsedSince(row.lastActivityAt, serverTime)}`
  if (status === 'break') {
    return row.breakStartedAt ? `Break since ${formatMaybeTime(row.breakStartedAt)}` : 'On break'
  }
  if (status === 'online') {
    if (row.attendanceStatus === 'clocked_in' && row.clockInAt) {
      return `In ${formatMaybeTime(row.clockInAt)}`
    }
    if (row.attendanceStatus === 'clocked_out' && row.clockOutAt) {
      return `Out ${formatMaybeTime(row.clockOutAt)}`
    }
    return 'Not clocked in'
  }
  if (isClockedIn(row) && row.clockInAt) {
    // Clocked in with no live connection: worth seeing, not an error.
    return `In ${formatMaybeTime(row.clockInAt)} · not connected`
  }
  return row.lastActivityAt ? `Last ${formatMaybeTime(row.lastActivityAt)}` : 'No activity'
}

function rowMatchesPersonFilter(row: WallboardMemberRow, filter: PersonFilter): boolean {
  if (filter === 'all') return true
  if (filter === 'clocked_in') return isClockedIn(row)
  return getMemberStatus(row) === filter
}

// One row per person for the Person tab. A member can belong to several
// companies; collapse to the row whose status ranks highest on the legend
// (no show > due soon > online > break > idle > offline), so the chips, the
// Person tab and the filters all agree. Ties go to the company they're
// clocked into.
function dedupePeople(companies: WallboardCompanySummary[]): WallboardMemberRow[] {
  const byUser = new Map<string, WallboardMemberRow>()
  for (const company of companies) {
    for (const row of company.members) {
      const current = byUser.get(row.user.id)
      if (!current) {
        byUser.set(row.user.id, row)
        continue
      }
      const diff =
        MEMBER_STATUS_PRIORITY[getMemberStatus(row)] -
        MEMBER_STATUS_PRIORITY[getMemberStatus(current)]
      if (diff < 0 || (diff === 0 && isClockedIn(row) && !isClockedIn(current))) {
        byUser.set(row.user.id, row)
      }
    }
  }
  return sortCompanyRoster(Array.from(byUser.values()))
}

// A presence patch never adds or removes a row, so `totalMembers` is left
// untouched here: on a truncated board it is the real roster size and stays
// larger than the rows we actually hold.
function countCompanyPresence(
  members: WallboardMemberRow[],
): Pick<
  WallboardCompanySummary,
  'clockedIn' | 'online' | 'idle' | 'offline' | 'onBreak' | 'noShow' | 'dueSoon'
> {
  let clockedIn = 0
  let online = 0
  let idle = 0
  let offline = 0
  let onBreak = 0
  let noShow = 0
  let dueSoon = 0
  for (const row of members) {
    if (row.presenceStatus === 'online') online += 1
    else if (row.presenceStatus === 'idle') idle += 1
    else offline += 1
    if (isClockedIn(row)) clockedIn += 1
    if (row.attendanceStatus === 'on_break') onBreak += 1
    const status = getMemberStatus(row)
    if (status === 'no_show') noShow += 1
    if (status === 'due_soon') dueSoon += 1
  }
  return { clockedIn, online, idle, offline, onBreak, noShow, dueSoon }
}

// The page reads only the scale numbers from totals (the chips are counted
// from rows). A capped board holds fewer rows than the roster — only offline
// people are dropped — so the server's member total is kept.
function recomputeTotals(
  companies: WallboardCompanySummary[],
  previous: WallboardBoard['totals'],
): WallboardBoard['totals'] {
  const people = new Set<string>()
  for (const company of companies) {
    for (const row of company.members) people.add(row.user.id)
  }
  return {
    ...previous,
    companies: companies.length,
    members: Math.max(previous.members, people.size),
  }
}

// Apply a realtime per-user patch: presence everywhere the person appears,
// and attendance + shift flags per company from the patch's own slice.
// `missing` is true when a slice is for a company on this board that holds no
// row for them (hidden on a capped board, or newly added there): only a
// reload can place them.
function applyPresenceEvent(
  board: WallboardBoard,
  event: WallboardPresenceEvent,
): { board: WallboardBoard; known: boolean; missing: boolean } {
  const byCompany = new Map(event.companies.map((slice) => [slice.companyId, slice]))
  let known = false
  let missing = false
  const companies = board.companies.map((company) => {
    const index = company.members.findIndex((row) => row.user.id === event.user.id)
    if (index === -1) {
      if (byCompany.has(company.id)) missing = true
      return company
    }
    known = true
    const slice = byCompany.get(company.id)
    const members = [...company.members]
    members[index] = {
      ...members[index],
      presenceStatus: event.presenceStatus,
      lastActivityAt: event.lastActivityAt,
      loginAt: event.loginAt ?? members[index].loginAt,
      ...(slice
        ? {
            attendanceStatus: slice.attendanceStatus,
            clockInAt: slice.clockInAt,
            clockOutAt: slice.clockOutAt,
            expectedEndAt: slice.expectedEndAt,
            breakStartedAt: slice.breakStartedAt,
            noShow: slice.noShow,
            dueSoon: slice.dueSoon,
            shiftStartAt: slice.shiftStartAt,
          }
        : {}),
    }
    return { ...company, members, ...countCompanyPresence(members) }
  })
  if (!known) return { board, known, missing }
  return {
    board: { ...board, companies, totals: recomputeTotals(companies, board.totals) },
    known,
    missing,
  }
}

/** Would a person in this patch show up on a board filtered by `query`? */
function patchCouldMatch(
  event: WallboardPresenceEvent,
  query: WallboardQuery,
  companyNames: Map<string, string>,
): boolean {
  const needle = query.search?.toLowerCase()
  if (needle) {
    const fields = [
      event.user.fullName,
      event.user.username,
      event.user.employeeId,
      ...event.companies.map((slice) => companyNames.get(slice.companyId) ?? ''),
    ]
    if (!fields.some((value) => value.toLowerCase().includes(needle))) return false
  }
  if (query.attendanceStatus?.length) {
    const wanted = new Set(query.attendanceStatus)
    if (!event.companies.some((slice) => wanted.has(slice.attendanceStatus))) return false
  }
  return true
}

// The board's status strip: one chip per dot colour, each carrying its own
// count and — on wide screens — a plain-language caption, so the legend and the
// numbers are the same object instead of two competing rows. Scale (members /
// companies) is deliberately uncoloured: it is context, not a status.
function BoardStatusStrip({
  counts,
  clockedIn,
  memberTotal,
  companyTotal,
  filtered = false,
  activeStatus,
  onStatusToggle,
  compact = false,
  layout = 'inline',
  className,
  children,
}: {
  counts: MemberStatusCounts
  /** People clocked in, of whom on break / not connected. */
  clockedIn: { total: number; onBreak: number; notConnected: number }
  memberTotal: number
  companyTotal: number
  /** Totals reflect a search or attendance filter, not the whole roster. */
  filtered?: boolean
  /** The status the board is currently filtered to, if any. */
  activeStatus?: Exclude<PersonFilter, 'all'> | null
  /** Omit to render the strip as a read-only key. */
  onStatusToggle?: (status: Exclude<PersonFilter, 'all'>) => void
  compact?: boolean
  /** 'tiles' is the command bar's grid of cards; 'inline' the one-line chips. */
  layout?: 'tiles' | 'inline'
  className?: string
  children?: React.ReactNode
}): React.ReactElement {
  const interactive = onStatusToggle != null
  const clockedInActive = activeStatus === 'clocked_in'
  const clockedInCaption = [
    clockedIn.onBreak > 0 ? `${clockedIn.onBreak} on break` : null,
    clockedIn.notConnected > 0 ? `${clockedIn.notConnected} not connected` : null,
  ]
    .filter(Boolean)
    .join(' · ')
  if (layout === 'tiles') {
    return (
      <StatusTiles
        counts={counts}
        clockedIn={clockedIn}
        clockedInCaption={clockedInCaption}
        memberTotal={memberTotal}
        companyTotal={companyTotal}
        filtered={filtered}
        activeStatus={activeStatus}
        onStatusToggle={onStatusToggle}
        compact={compact}
        className={className}
      >
        {children}
      </StatusTiles>
    )
  }
  return (
    <div className={cn('flex flex-wrap items-center gap-x-3 gap-y-2', className)}>
      {/* The headline: who is at work. Set apart from the dots because it is
          attendance, not connection — it overlaps every presence status. */}
      <button
        type="button"
        disabled={!interactive}
        aria-pressed={interactive ? clockedInActive : undefined}
        title={
          interactive
            ? `${CLOCKED_IN_META.hint}. Click to ${clockedInActive ? 'clear the filter' : 'show only people clocked in'}.`
            : CLOCKED_IN_META.hint
        }
        onClick={() => onStatusToggle?.('clocked_in')}
        className={cn(
          'inline-flex items-center gap-2 rounded-xl border-2 px-3 text-left transition disabled:cursor-default',
          compact ? 'py-1' : 'py-1.5',
          clockedInActive
            ? 'border-primary bg-primary/15'
            : 'border-primary/40 bg-primary/5 hover:border-primary/60',
        )}
      >
        <span className="text-lg font-bold leading-none tabular-nums text-primary">
          {clockedIn.total}
        </span>
        <span className="flex min-w-0 flex-col leading-tight">
          <span className="text-xs font-semibold text-gray-800 dark:text-gray-100">
            {CLOCKED_IN_META.label}
          </span>
          {compact || !clockedInCaption ? null : (
            <span className="text-[10px] text-gray-500 dark:text-gray-400">{clockedInCaption}</span>
          )}
        </span>
      </button>

      <div
        className="flex flex-wrap items-center gap-1.5"
        role={interactive ? 'group' : 'list'}
        aria-label={interactive ? 'Filter the board by status' : 'Presence status key'}
      >
        {MEMBER_STATUS_ORDER.map((status) => {
          const meta = MEMBER_STATUS_META[status]
          const active = activeStatus === status
          const content = (
            <>
              <span
                className={cn(
                  'h-2 w-2 shrink-0 rounded-full',
                  meta.dot,
                  // A dimmed dot would read as "this status is off", so the
                  // unpicked chips keep their colour and lose only contrast.
                  activeStatus != null && !active && 'opacity-60',
                )}
                aria-hidden
              />
              <span className={cn('text-base font-semibold leading-none tabular-nums', meta.tint)}>
                {counts[status]}
              </span>
              <span className="flex min-w-0 flex-col leading-tight">
                <span className="text-xs font-medium text-gray-700 dark:text-gray-200">
                  {meta.label}
                </span>
                {compact ? null : (
                  <span className="hidden text-[10px] text-gray-400 dark:text-gray-500 lg:block">
                    {meta.caption}
                  </span>
                )}
              </span>
            </>
          )
          const shell = cn(
            'inline-flex items-center gap-2 rounded-xl border px-2.5 text-left transition',
            compact ? 'py-1' : 'py-1.5',
            active
              ? 'border-primary/40 bg-primary/10 ring-1 ring-primary/30'
              : cn(
                  'border-gray-200 bg-gray-50/80 dark:border-gray-700 dark:bg-gray-900/40',
                  activeStatus != null && 'opacity-70',
                ),
          )
          if (!interactive) {
            return (
              <div
                key={status}
                role="listitem"
                title={`${meta.label} - ${meta.hint}`}
                className={shell}
              >
                {content}
              </div>
            )
          }
          return (
            <button
              key={status}
              type="button"
              aria-pressed={active}
              title={`${meta.hint}. Click to ${active ? 'clear the filter' : `show only ${meta.label.toLowerCase()}`}.`}
              onClick={() => onStatusToggle(status)}
              className={cn(shell, 'hover:border-gray-300 dark:hover:border-gray-600')}
            >
              {content}
            </button>
          )
        })}
      </div>

      <div className="flex items-center gap-2.5 text-xs text-gray-500 dark:text-gray-400">
        <span>
          <span className="font-semibold tabular-nums text-gray-900 dark:text-gray-100">
            {memberTotal}
          </span>{' '}
          members
          {filtered ? (
            <span className="ml-1 rounded bg-gray-100 px-1 text-[10px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
              filtered
            </span>
          ) : null}
        </span>
        <span className="text-gray-300 dark:text-gray-600">/</span>
        <span>
          <span className="font-semibold tabular-nums text-gray-900 dark:text-gray-100">
            {companyTotal}
          </span>{' '}
          companies
        </span>
      </div>

      {children}
    </div>
  )
}

/**
 * The command bar's status cards: one even grid (seven across on wide
 * screens) with clocked-in first, a coloured bar per status, and the scale
 * (members / companies) plus the connection badge on their own line above, so
 * nothing is left wrapping on a row by itself. Each card filters the board.
 */
function StatusTiles({
  counts,
  clockedIn,
  clockedInCaption,
  memberTotal,
  companyTotal,
  filtered,
  activeStatus,
  onStatusToggle,
  compact,
  className,
  children,
}: {
  counts: MemberStatusCounts
  clockedIn: { total: number }
  clockedInCaption: string
  memberTotal: number
  companyTotal: number
  filtered: boolean
  activeStatus?: Exclude<PersonFilter, 'all'> | null
  onStatusToggle?: (status: Exclude<PersonFilter, 'all'>) => void
  compact: boolean
  className?: string
  children?: React.ReactNode
}): React.ReactElement {
  const interactive = onStatusToggle != null
  const anyActive = activeStatus != null
  const tiles: Array<{
    key: Exclude<PersonFilter, 'all'>
    label: string
    caption: string
    count: number
    bar: string
    tint: string
    selected: string
    hint: string
  }> = [
    {
      key: 'clocked_in',
      label: CLOCKED_IN_META.label,
      caption: clockedInCaption || 'at work right now',
      count: clockedIn.total,
      bar: 'bg-primary',
      tint: 'text-primary dark:text-sky-300',
      selected:
        'border-primary/50 bg-primary/10 ring-2 ring-primary/25 dark:border-primary/60 dark:bg-primary/20',
      hint: CLOCKED_IN_META.hint,
    },
    ...MEMBER_STATUS_ORDER.map((status) => {
      const meta = MEMBER_STATUS_META[status]
      return {
        key: status,
        label: meta.label,
        caption: meta.caption,
        count: counts[status],
        bar: meta.dot,
        tint: meta.tint,
        selected: meta.selected,
        hint: meta.hint,
      }
    }),
  ]

  return (
    <div className={cn('flex flex-col gap-2.5', className)}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-gray-500 dark:text-gray-400">
        <span>
          <span className="font-semibold tabular-nums text-gray-900 dark:text-gray-100">
            {memberTotal}
          </span>{' '}
          members
        </span>
        <span className="text-gray-300 dark:text-gray-600" aria-hidden>
          ·
        </span>
        <span>
          <span className="font-semibold tabular-nums text-gray-900 dark:text-gray-100">
            {companyTotal}
          </span>{' '}
          companies
        </span>
        {filtered ? (
          <span className="rounded-md bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
            filtered
          </span>
        ) : null}
        {children}
      </div>

      <div
        className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-7"
        role={interactive ? 'group' : 'list'}
        aria-label={interactive ? 'Filter the board by status' : 'Presence status key'}
      >
        {tiles.map((tile) => {
          const active = activeStatus === tile.key
          const body = (
            <>
              <span
                className={cn('absolute inset-x-0 top-0 h-1', tile.bar, anyActive && !active && 'opacity-50')}
                aria-hidden
              />
              <span className="flex items-center justify-between gap-2">
                <span className="truncate text-xs font-semibold text-gray-700 dark:text-gray-200">
                  {tile.label}
                </span>
                {compact ? (
                  <span
                    className={cn(
                      'text-base font-bold leading-none tabular-nums',
                      tile.count === 0 ? 'text-gray-300 dark:text-gray-600' : tile.tint,
                    )}
                  >
                    {tile.count}
                  </span>
                ) : null}
              </span>
              {compact ? null : (
                <>
                  <span
                    className={cn(
                      'text-2xl font-bold leading-none tracking-tight tabular-nums',
                      // A zero fades back so the numbers that need attention stand out.
                      tile.count === 0 ? 'text-gray-300 dark:text-gray-600' : tile.tint,
                    )}
                  >
                    {tile.count}
                  </span>
                  <span className="truncate text-[11px] text-gray-500 dark:text-gray-400">
                    {tile.caption}
                  </span>
                </>
              )}
            </>
          )
          const shell = cn(
            'relative flex min-w-0 flex-col overflow-hidden rounded-xl border text-left transition',
            compact ? 'gap-0 px-3 pb-1.5 pt-2.5' : 'gap-1.5 px-3 pb-2.5 pt-3.5',
            active
              ? tile.selected
              : cn(
                  'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800/60',
                  anyActive && 'opacity-60',
                ),
          )
          if (!interactive) {
            return (
              <div key={tile.key} role="listitem" title={tile.hint} className={shell}>
                {body}
              </div>
            )
          }
          return (
            <button
              key={tile.key}
              type="button"
              aria-pressed={active}
              title={`${tile.hint}. Click to ${active ? 'clear the filter' : `show only ${tile.label.toLowerCase()}`}.`}
              onClick={() => onStatusToggle(tile.key)}
              className={cn(
                shell,
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
                !active && 'hover:border-gray-300 hover:shadow-sm hover:opacity-100 dark:hover:border-gray-600',
              )}
            >
              {body}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export function WallboardPage(): React.ReactElement {
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const { socket, isConnected } = useSocket()
  const { data: myCompanies = [] } = useCompanyNavList()
  const [activeView, setActiveView] = useState<WallboardView>('company')
  // One status lens for the whole board: the header chips, the company card
  // rosters and the Person tab all read and write this.
  const [statusFilter, setStatusFilter] = useState<PersonFilter>('all')
  const [cardAppearance, setCardAppearance] = useState<CardAppearance>(() => {
    const saved = window.localStorage.getItem(CARD_APPEARANCE_KEY)
    return saved === 'light' ? saved : 'dark'
  })
  // Full-screen "update view": all company cards at once, max container width,
  // no chrome — meant for a wall-mounted display. Esc exits.
  const [isFullscreen, setIsFullscreen] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const searchDebounceRef = useRef<number | null>(null)
  // Collapse the sticky command bar to a slim strip once the user scrolls past
  // a small threshold: the description hides and vertical padding shrinks so the
  // wallboard grid keeps most of the viewport.
  const [isBarCollapsed, setIsBarCollapsed] = useState(false)

  const params = useMemo<WallboardQuery>(() => parseWallboardSearchParams(searchParams), [searchParams])
  const paramsKey = useMemo(() => buildWallboardSearchParams(params).toString(), [params])
  const wallboardQuery = useWallboard(params)

  // Live board: seeded from the REST fetch, patched by socket presence events.
  const [liveBoard, setLiveBoard] = useState<{ key: string; board: WallboardBoard } | null>(null)

  // Adopt every fresh REST board (initial load + 45s backstop refetch) as the
  // new base so roster changes and any missed patch self-heal.
  useEffect(() => {
    if (!wallboardQuery.data) return
    setLiveBoard({ key: paramsKey, board: wallboardQuery.data })
  }, [wallboardQuery.data, paramsKey])

  const board = liveBoard?.key === paramsKey ? liveBoard.board : (wallboardQuery.data ?? null)
  const companies = useMemo(() => board?.companies ?? [], [board])
  const totals = board?.totals
  const people = useMemo(() => dedupePeople(companies), [companies])
  // "Clocked in" is per company: someone on break in A and idle in B is
  // clocked in, so pick their clocked-in row rather than their dot row.
  const clockedInPeople = useMemo(
    () =>
      dedupePeople(
        companies.map((company) => ({
          ...company,
          members: company.members.filter(isClockedIn),
        })),
      ),
    [companies],
  )
  const filteredPeople = useMemo(
    () =>
      statusFilter === 'clocked_in'
        ? clockedInPeople
        : people.filter((row) => rowMatchesPersonFilter(row, statusFilter)),
    [clockedInPeople, people, statusFilter],
  )
  const statusCounts = useMemo(
    () => countMemberStatuses(people, totals?.members ?? people.length),
    [people, totals?.members],
  )
  // Distinct people clocked in anywhere (a person clocked into one company
  // and idle in another still counts once).
  const clockedInCounts = useMemo(() => {
    const atWork = new Map<string, { onBreak: boolean; notConnected: boolean }>()
    for (const company of companies) {
      for (const row of company.members) {
        if (!isClockedIn(row)) continue
        const prev = atWork.get(row.user.id)
        atWork.set(row.user.id, {
          onBreak: (prev?.onBreak ?? false) || row.attendanceStatus === 'on_break',
          notConnected: (prev?.notConnected ?? true) && row.presenceStatus === 'offline',
        })
      }
    }
    let onBreak = 0
    let notConnected = 0
    for (const person of atWork.values()) {
      if (person.onBreak) onBreak += 1
      if (person.notConnected) notConnected += 1
    }
    return { total: atWork.size, onBreak, notConnected }
  }, [companies])
  // Search and attendance filters are applied on the server, so the headline
  // numbers describe the filtered set — label them, and don't trust patches
  // (which can't add or drop rows) to keep such a board correct.
  const isFilteredBoard = Boolean(params.search) || (params.attendanceStatus?.length ?? 0) > 0

  const selectedCompanyIds = useMemo(() => new Set(params.companyIds ?? []), [params.companyIds])
  const allowedCompanyIds = board?.allowedCompanyIds
  const allowedCompanyIdsKey = allowedCompanyIds?.join(',') ?? ''
  const connectionBadge = getConnectionBadge(isConnected)

  const companyOptions = myCompanies.filter((company) =>
    allowedCompanyIds ? allowedCompanyIds.includes(company._id) : true,
  )

  const updateQuery = useCallback(
    (updater: (current: WallboardQuery) => WallboardQuery) => {
      const next = normalizeWallboardQuery(updater(normalizeWallboardQuery(params)))
      setSearchParams(buildWallboardSearchParams(next))
    },
    [params, setSearchParams],
  )

  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.value = params.search ?? ''
    }
  }, [params.search])

  // Drop any selected company the viewer is no longer allowed to see.
  useEffect(() => {
    if (!allowedCompanyIds || params.companyIds == null || params.companyIds.length === 0) return
    const filteredIds = params.companyIds.filter((companyId) => allowedCompanyIds.includes(companyId))
    if (filteredIds.length === params.companyIds.length) return
    updateQuery((current) => ({
      ...current,
      companyIds: filteredIds.length > 0 ? filteredIds : undefined,
    }))
  }, [allowedCompanyIds, allowedCompanyIdsKey, params.companyIds, updateQuery])

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current != null) {
        window.clearTimeout(searchDebounceRef.current)
      }
    }
  }, [])

  // Collapse the command bar after a little scroll. Hysteresis (collapse at 96px,
  // expand back near the top) avoids flicker when hovering the threshold.
  useEffect(() => {
    const scroller = document.querySelector<HTMLElement>('[data-app-scroll]') ?? null
    const target: HTMLElement | Window = scroller ?? window
    const readTop = (): number => (scroller ? scroller.scrollTop : window.scrollY)
    const handleScroll = (): void => {
      const top = readTop()
      setIsBarCollapsed((prev) => (prev ? top > 24 : top > 96))
    }
    handleScroll()
    target.addEventListener('scroll', handleScroll, { passive: true })
    return () => target.removeEventListener('scroll', handleScroll)
  }, [])

  useFullscreenShortcut({
    isFullscreen,
    setIsFullscreen,
    canEnter: companies.length > 0,
  })

  // Realtime wallboard feed. Patches are applied to a ref first, outside the
  // state updater, so the reload decision is made on the board they actually
  // landed on; React may run an updater later than the call. The REST data
  // is read through a ref too, so a 45s refetch doesn't re-subscribe (each
  // subscribe makes the server build a full snapshot).
  const restBoardRef = useRef<WallboardBoard | undefined>(wallboardQuery.data)
  const liveBoardRef = useRef(liveBoard)
  useEffect(() => {
    restBoardRef.current = wallboardQuery.data
  }, [wallboardQuery.data])
  useEffect(() => {
    liveBoardRef.current = liveBoard
  }, [liveBoard])
  const refetchTimerRef = useRef<number | null>(null)
  // Newest patch applied per person; an older one arriving late is dropped.
  const patchBuiltAtRef = useRef(new Map<string, string>())

  useEffect(() => {
    if (!socket) return
    const subscribePayload = { ...params, requestKey: paramsKey }
    const listKey = wallboardQueryKeys.list(params)
    const patchBuiltAt = patchBuiltAtRef.current
    patchBuiltAt.clear()

    // Coalesce bursts of patches that need a full reload into one refetch.
    const scheduleRefetch = (): void => {
      if (refetchTimerRef.current != null) return
      refetchTimerRef.current = window.setTimeout(() => {
        refetchTimerRef.current = null
        void queryClient.invalidateQueries({ queryKey: listKey })
      }, 1500)
    }

    const handleSnapshot = (snapshot: WallboardBoard & { requestKey?: string }): void => {
      // A late snapshot for the previous filters must not land under these.
      if (snapshot.requestKey !== undefined && snapshot.requestKey !== paramsKey) return
      const next = { key: paramsKey, board: snapshot }
      liveBoardRef.current = next
      setLiveBoard(next)
    }

    const handlePresence = (event: WallboardPresenceEvent): void => {
      const seen = patchBuiltAt.get(event.user.id)
      if (seen && event.builtAt && event.builtAt < seen) return
      if (event.builtAt) patchBuiltAt.set(event.user.id, event.builtAt)

      const current = liveBoardRef.current
      const base = current?.key === paramsKey ? current.board : restBoardRef.current
      if (!base) return
      const result = applyPresenceEvent(base, event)
      const next = { key: paramsKey, board: result.board }
      liveBoardRef.current = next
      setLiveBoard(next)

      // Patches can't add or drop rows, so reload when one should: the person
      // belongs on this board but has no row here, or a filtered board may
      // now include or exclude them. Patches for other companies never do.
      const onBoard = event.companies.some((slice) =>
        base.companies.some((company) => company.id === slice.companyId),
      )
      if (!onBoard) return
      const names = new Map(base.companies.map((company) => [company.id, company.name]))
      const needsReload = isFilteredBoard
        ? result.known || patchCouldMatch(event, params, names)
        : result.missing
      if (needsReload) scheduleRefetch()
    }

    const handleConnect = (): void => {
      void queryClient.invalidateQueries({ queryKey: listKey })
      socket.emit('wallboard.subscribe', subscribePayload)
    }

    socket.on('wallboard.snapshot', handleSnapshot)
    socket.on('wallboard.presence', handlePresence)
    socket.on('connect', handleConnect)
    socket.emit('wallboard.subscribe', subscribePayload)

    return () => {
      socket.off('wallboard.snapshot', handleSnapshot)
      socket.off('wallboard.presence', handlePresence)
      socket.off('connect', handleConnect)
      if (refetchTimerRef.current != null) {
        window.clearTimeout(refetchTimerRef.current)
        refetchTimerRef.current = null
      }
    }
  }, [isFilteredBoard, params, paramsKey, queryClient, socket])

  const handleCompanyToggle = useCallback(
    (companyId: string | null): void => {
      updateQuery((current) => {
        if (companyId === null) {
          return { ...current, companyIds: undefined }
        }
        const selected = new Set(current.companyIds ?? [])
        if (selected.has(companyId)) selected.delete(companyId)
        else selected.add(companyId)
        return {
          ...current,
          companyIds: selected.size > 0 ? Array.from(selected).sort() : undefined,
        }
      })
    },
    [updateQuery],
  )

  const handleRefresh = useCallback(() => {
    void wallboardQuery.refetch()
    if (socket) socket.emit('wallboard.subscribe', params)
  }, [params, socket, wallboardQuery])

  const changeCardAppearance = useCallback((appearance: CardAppearance) => {
    setCardAppearance(appearance)
    window.localStorage.setItem(CARD_APPEARANCE_KEY, appearance)
  }, [])

  const cardAppearanceControl = (
    <div
      className="flex items-center rounded-lg border border-gray-200 bg-white p-0.5 dark:border-gray-700 dark:bg-gray-800"
      role="group"
      aria-label="Company card appearance"
    >
      {([
        { value: 'light', label: 'Light cards', icon: Sun },
        { value: 'dark', label: 'Dark cards', icon: Moon },
      ] as const).map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => changeCardAppearance(option.value)}
          aria-label={option.label}
          aria-pressed={cardAppearance === option.value}
          title={option.label}
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-md text-gray-500 transition hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100',
            cardAppearance === option.value &&
              'bg-primary text-white hover:bg-primary hover:text-white dark:bg-primary dark:text-white',
          )}
        >
          <option.icon className="h-3.5 w-3.5" aria-hidden />
        </button>
      ))}
    </div>
  )

  const clearSearch = useCallback(() => {
    updateQuery((current) => ({ ...current, search: undefined }))
  }, [updateQuery])

  const handleStatusToggle = useCallback((status: Exclude<PersonFilter, 'all'>) => {
    setStatusFilter((current) => (current === status ? 'all' : status))
  }, [])

  const clearStatusFilter = useCallback(() => setStatusFilter('all'), [])

  // With a status picked, a company with nobody in that state is noise on the
  // grid — 52 empty cards to scroll past — so it drops out entirely.
  const visibleCompanies = useMemo(() => {
    if (statusFilter === 'all') return companies
    return companies.filter((company) =>
      company.members.some((row) => rowMatchesPersonFilter(row, statusFilter)),
    )
  }, [companies, statusFilter])

  const hasData = companies.length > 0
  const hasVisibleCompanies = visibleCompanies.length > 0

  // Single company card — shared by the normal grid and the full-screen view.
  const renderCompanyCard = (company: WallboardCompanySummary): React.ReactElement => {
    // The accent is the company's configured hex (set in company settings);
    // when none is set we fall back to the deterministic per-company palette
    // tint used elsewhere. It marks the card's top rule in both appearances.
    const accent = getCompanyColor(company.id, company.color).dot
    const darkCards = cardAppearance === 'dark'
    const roster = sortCompanyRoster(
      statusFilter === 'all'
        ? company.members
        : company.members.filter((row) => rowMatchesPersonFilter(row, statusFilter)),
    )
    const hiddenCount = getHiddenMemberCount(company)
    const needsAttention = getCompanyAttentionScore(company) > 0
    const primaryText = darkCards ? 'text-white' : 'text-gray-950 dark:text-gray-100'
    const secondaryText = darkCards
      ? 'text-white/75'
      : 'text-gray-600 dark:text-gray-300'
    const mutedText = darkCards
      ? 'text-white/55'
      : 'text-gray-500 dark:text-gray-400'
    return (
      <Card
        key={company.id}
        className={cn(
          'relative overflow-hidden rounded-2xl border shadow-[0_16px_40px_-28px_rgba(15,23,42,0.5)]',
          darkCards ? 'text-white' : 'text-gray-950 dark:text-gray-100',
        )}
        style={{
          // Dark mode is a true black card so the status dots and names carry
          // the colour; the company's own hex survives as the top rule and a
          // faint edge tint rather than flooding the whole surface.
          backgroundColor: darkCards ? '#0A0A0A' : '#ffffff',
          borderColor: darkCards ? hexToRgba(accent, 0.45) : hexToRgba(accent, 0.3),
        }}
        contentClassName="space-y-3 p-4 pt-5"
      >
        <div
          className={cn('absolute inset-x-0 top-0', darkCards ? 'h-1.5' : 'h-1')}
          style={{ backgroundColor: accent }}
          aria-hidden
        />
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className={cn('truncate text-base font-semibold tracking-tight', primaryText)}>
              {company.name}
            </h2>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <p className={cn('text-xs', secondaryText)}>
                <span className="font-semibold">{company.clockedIn}</span> clocked in ·{' '}
                {countOnlineDots(company)}/
                {company.totalMembers} online
              </p>
              {company.noShow > 0 ? (
                <span
                  className={cn(
                    'inline-flex items-center gap-1 text-xs font-semibold',
                    darkCards ? 'text-rose-300' : 'text-rose-600 dark:text-rose-400',
                  )}
                >
                  <span
                    className={cn(
                      'h-1.5 w-1.5 rounded-full',
                      darkCards ? 'bg-rose-400' : 'bg-rose-500',
                    )}
                    aria-hidden
                  />
                  {company.noShow} no show
                </span>
              ) : null}
              {/* The light Badge pills are far too bright against a black card,
                  so dark cards get a low-key tinted pill instead. */}
              {darkCards ? (
                <span
                  className={cn(
                    'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold leading-5',
                    needsAttention
                      ? 'bg-amber-400/15 text-amber-200'
                      : 'bg-emerald-400/15 text-emerald-200',
                  )}
                >
                  {needsAttention ? 'Needs attention' : 'Normal'}
                </span>
              ) : (
                <Badge variant={needsAttention ? 'warning' : 'success'}>
                  {needsAttention ? 'Needs attention' : 'Normal'}
                </Badge>
              )}
            </div>
          </div>
          <div
            className={cn(
              'flex h-8 w-8 shrink-0 items-center justify-center rounded-xl',
              darkCards
                ? 'bg-white/10 text-white'
                : 'bg-white/80 text-gray-700 dark:bg-gray-900/70 dark:text-gray-200',
            )}
          >
            <Building2 className="h-4 w-4" aria-hidden />
          </div>
        </div>

        {/* Roster: one line per person, status carried entirely by the dot
            colour (see the legend in the command bar). */}
        <div
          className={cn(
            'overflow-hidden rounded-2xl border',
            darkCards
              ? 'border-white/10 bg-white/[0.04]'
              : 'border-gray-200/80 bg-white/70 dark:border-gray-700 dark:bg-gray-900/50',
          )}
        >
          <div className="max-h-64 space-y-0.5 overflow-y-auto p-1.5">
            {roster.length > 0 ? (
              roster.map((row) => {
                const status = getMemberStatus(row)
                const meta = MEMBER_STATUS_META[status]
                return (
                  <div
                    key={`${company.id}:${row.user.id}`}
                    title={`${row.user.fullName} (${row.user.employeeId}) - ${meta.label}: ${meta.hint}`}
                    className={cn(
                      'flex items-center gap-2.5 rounded-xl px-2.5 py-1.5 text-sm transition-colors',
                      darkCards ? 'hover:bg-white/10' : 'hover:bg-white dark:hover:bg-gray-800/70',
                    )}
                  >
                    <span
                      className={cn(
                        'h-2.5 w-2.5 shrink-0 rounded-full',
                        darkCards ? meta.dotOnDark : meta.dot,
                      )}
                      aria-hidden
                    />
                    <span className="sr-only">{meta.label}: </span>
                    <span className={cn('min-w-0 flex-1 truncate font-medium', primaryText)}>
                      {row.user.fullName}
                    </span>
                    <span className={cn('shrink-0 text-xs tabular-nums', mutedText)}>
                      {getMemberDetail(row, status, board?.serverTime)}
                    </span>
                  </div>
                )
              })
            ) : (
              <p className={cn('px-2.5 py-3 text-sm', mutedText)}>No members to show.</p>
            )}
          </div>
          {hiddenCount > 0 ? (
            <p
              className={cn(
                'border-t px-3 py-1.5 text-[11px]',
                darkCards
                  ? 'border-white/10 text-white/55'
                  : 'border-gray-200/80 text-gray-500 dark:border-gray-700 dark:text-gray-400',
              )}
            >
              +{hiddenCount} offline not shown
            </p>
          ) : null}
        </div>
      </Card>
    )
  }

  // Full-screen "update view": every company card at once, max width, no footer.
  if (isFullscreen) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-stone-50 dark:bg-gray-950">
        <div className="flex items-center justify-between gap-3 border-b border-stone-200 px-6 py-3 dark:border-gray-800">
          <div className="flex items-center gap-3">
            <Tv className="h-5 w-5 text-primary" aria-hidden />
            <h1 className="text-lg font-semibold tracking-tight text-gray-950 dark:text-gray-100">
              WallBoard
            </h1>
            <Badge variant={connectionBadge.variant} className="gap-1.5">
              <connectionBadge.icon className="h-3.5 w-3.5" aria-hidden />
              {connectionBadge.label}
            </Badge>
            <BoardStatusStrip
              counts={statusCounts}
              clockedIn={clockedInCounts}
              filtered={isFilteredBoard}
              memberTotal={totals?.members ?? 0}
              companyTotal={totals?.companies ?? 0}
              activeStatus={statusFilter === 'all' ? null : statusFilter}
              onStatusToggle={handleStatusToggle}
              className="hidden md:flex"
            />
          </div>
          <div className="flex items-center gap-3">
            {cardAppearanceControl}
            <Button
              variant="secondary"
              size="sm"
              className="gap-1.5"
              onClick={handleRefresh}
              disabled={wallboardQuery.isFetching}
            >
              <RefreshCw
                className={cn('h-3.5 w-3.5', wallboardQuery.isFetching && 'animate-spin')}
                aria-hidden
              />
              Refresh
            </Button>
            <Button
              variant="secondary"
              size="sm"
              className="gap-1.5"
              onClick={() => setIsFullscreen(false)}
              title="Exit full screen (press F or Esc)"
            >
              <Minimize2 className="h-3.5 w-3.5" aria-hidden />
              Exit
              <kbd className="ml-1.5 hidden rounded border border-gray-300 bg-gray-50 px-1.5 py-0.5 font-sans text-[10px] font-semibold leading-none text-gray-500 sm:inline-block dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300">
                F
              </kbd>
              <kbd className="ml-1.5 hidden rounded border border-gray-300 bg-gray-50 px-1.5 py-0.5 font-sans text-[10px] font-semibold leading-none text-gray-500 sm:inline-block dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300">
                Esc
              </kbd>
            </Button>
          </div>
        </div>
        {/* All cards simultaneously, using the full container width. */}
        <div
          className="flex-1 overflow-y-auto p-6"
          role="region"
          aria-label="Company wallboard cards (full screen)"
        >
          {hasVisibleCompanies ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
              {visibleCompanies.map(renderCompanyCard)}
            </div>
          ) : (
            <EmptyState
              icon={Tv}
              title={statusFilter === 'all' ? 'No members found' : 'Nobody in this status'}
              description={
                statusFilter === 'all'
                  ? 'Try a different company or search.'
                  : `No member is ${filterLabel(statusFilter).toLowerCase()} right now.`
              }
            />
          )}
        </div>
      </div>
    )
  }

  return (
    <PageShell
      className="-mb-5 gap-0 sm:-mb-6 lg:-mb-8"
      breadcrumbItems={[{ label: 'Time & Attendance', to: '/dashboard' }, { label: 'WallBoard' }]}
      icon={Tv}
      title="WallBoard"
      description="Live company and person presence."
      hideHeader
    >
      <div className="space-y-5">
        {/* Unified command bar, two rows: what the board says (status strip +
            connection) above what you do to it (scope, search, view, actions).
            Sticks under the app Header (h-14) and tightens to a slim strip on
            scroll to hand the viewport back to the wallboard grid. */}
        <Card
          className={cn(
            'sticky top-14 z-20 rounded-3xl border-stone-200 bg-white/95 shadow-sm backdrop-blur supports-backdrop-filter:bg-white/80 dark:border-gray-700 dark:bg-gray-800/95 dark:supports-backdrop-filter:bg-gray-800/80',
          )}
          contentClassName={cn(
            'transition-[padding] duration-200',
            isBarCollapsed ? 'space-y-2.5 p-3' : 'space-y-3.5 p-4',
          )}
        >
          {/* Row 1 — the status strip: counts and legend in one object, with
              the connection badge parked at the far right as the board's other
              piece of live state. */}
          <BoardStatusStrip
            counts={statusCounts}
            clockedIn={clockedInCounts}
            filtered={isFilteredBoard}
            memberTotal={totals?.members ?? 0}
            companyTotal={totals?.companies ?? 0}
            activeStatus={statusFilter === 'all' ? null : statusFilter}
            onStatusToggle={handleStatusToggle}
            compact={isBarCollapsed}
            layout="tiles"
          >
            <Badge variant={connectionBadge.variant} className="ml-auto gap-1.5">
              <connectionBadge.icon className="h-3.5 w-3.5" aria-hidden />
              {connectionBadge.label}
            </Badge>
          </BoardStatusStrip>

          {/* Filtering hides companies with nobody in that state, so say so —
              otherwise the grid looks like it lost cards. */}
          {statusFilter !== 'all' ? (
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-primary/30 bg-primary/5 px-3 py-1.5 text-xs text-gray-700 dark:border-primary/40 dark:bg-primary/10 dark:text-gray-200">
              <span
                className={cn(
                  'h-2 w-2 shrink-0 rounded-full',
                  filterDot(statusFilter),
                )}
                aria-hidden
              />
              <span>
                Showing only{' '}
                <span className="font-semibold">
                  {filterLabel(statusFilter).toLowerCase()}
                </span>{' '}
                · {visibleCompanies.length} of {companies.length} companies
              </span>
              <button
                type="button"
                onClick={clearStatusFilter}
                className="ml-auto inline-flex items-center gap-1 rounded-lg px-2 py-1 font-medium text-primary transition hover:bg-primary/10"
              >
                <X className="h-3 w-3" aria-hidden />
                Clear
              </button>
            </div>
          ) : null}

          {board && board.shiftStatusAvailable === false ? (
            <div
              role="status"
              className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200"
            >
              No show and Due soon can't be worked out right now, so they show 0. Everything else is
              live.
            </div>
          ) : null}

          {/* Row 2 — the command bar. Scope selector · search hero · view toggle. */}
          <div className="flex flex-col gap-2.5 lg:flex-row lg:items-center">
            <div className="shrink-0">
              <CompanyChips
                companies={companyOptions}
                selectedCompanyIds={selectedCompanyIds}
                onToggle={handleCompanyToggle}
              />
            </div>

            <div className="relative min-w-0 flex-1">
              <Search
                className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                aria-hidden
              />
              <input
                ref={searchInputRef}
                type="search"
                aria-label="Search wallboard"
                defaultValue={params.search ?? ''}
                placeholder="Search name, employee ID, username, or company"
                className="box-border h-11 w-full rounded-xl border border-gray-200 bg-white/95 pl-10 pr-10 text-sm text-gray-900 shadow-[0_1px_2px_rgba(15,23,42,0.05)] transition-all duration-150 placeholder:text-gray-400 focus:border-primary/40 focus:outline-none focus:ring-4 focus:ring-primary/10 dark:border-gray-700 dark:bg-gray-800/90 dark:text-gray-100 dark:placeholder:text-gray-500"
                onChange={(event) => {
                  const nextValue = event.target.value
                  if (searchDebounceRef.current != null) {
                    window.clearTimeout(searchDebounceRef.current)
                  }
                  searchDebounceRef.current = window.setTimeout(() => {
                    updateQuery((current) => ({
                      ...current,
                      search: nextValue.trim() || undefined,
                    }))
                  }, 250)
                }}
              />
              {params.search ? (
                <button
                  type="button"
                  onClick={() => {
                    clearSearch()
                    if (searchInputRef.current) searchInputRef.current.value = ''
                  }}
                  aria-label="Clear search"
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-md p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
              ) : null}
            </div>

            {/* Company / Person segmented toggle, attached to the bar's right. */}
            <div
              role="tablist"
              aria-label="Wallboard view"
              className="inline-flex shrink-0 rounded-xl border border-gray-200 bg-gray-50 p-1 dark:border-gray-700 dark:bg-gray-900/60"
            >
              {(
                [
                  { value: 'company' as const, label: 'Company', icon: Building2 },
                  { value: 'person' as const, label: 'Person', icon: UserRound },
                ]
              ).map(({ value, label, icon: Icon }) => {
                const active = activeView === value
                return (
                  <button
                    key={value}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setActiveView(value)}
                    className={cn(
                      'inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors lg:flex-none',
                      active
                        ? 'bg-white text-primary shadow-sm dark:bg-gray-800'
                        : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100',
                    )}
                  >
                    <Icon className="h-4 w-4" aria-hidden />
                    {label}
                  </button>
                )
              })}
            </div>

            {/* Live-status + presentation actions, moved off the (now hidden) page header
                into the command bar so the breadcrumb can live in the top app header. */}
            <div className="flex flex-wrap items-center gap-2 lg:ml-auto">
              {cardAppearanceControl}
              <Button
                variant="secondary"
                size="sm"
                className="gap-1.5"
                onClick={handleRefresh}
                disabled={wallboardQuery.isFetching}
              >
                <RefreshCw className={cn('h-3.5 w-3.5', wallboardQuery.isFetching && 'animate-spin')} aria-hidden />
                Refresh
              </Button>
              <Button
                variant="secondary"
                size="sm"
                className="gap-1.5"
                onClick={() => setIsFullscreen(true)}
                disabled={!hasData}
                title="Show all company cards on a full-screen display (press F)"
              >
                <Maximize2 className="h-3.5 w-3.5" aria-hidden />
                Full screen
                <kbd className="ml-1.5 hidden rounded border border-gray-300 bg-gray-50 px-1.5 py-0.5 font-sans text-[10px] font-semibold leading-none text-gray-500 sm:inline-block dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300">
                  F
                </kbd>
              </Button>
            </div>
          </div>
        </Card>

        {wallboardQuery.isLoading && !board ? (
          <Card className="rounded-[28px] border-stone-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800" contentClassName="p-10 text-center text-sm text-gray-500 dark:text-gray-400">
            Loading WallBoard...
          </Card>
        ) : wallboardQuery.isError && !board ? (
          <QueryErrorState
            error={wallboardQuery.error}
            title="Couldn't load the WallBoard"
            onRetry={wallboardQuery.refetch}
            className="rounded-[28px]"
          />
        ) : !hasData ? (
          <EmptyState icon={Tv} title="No members found" description="Try a different company or search." />
        ) : (
          <Tabs value={activeView} onValueChange={(value) => setActiveView(value as WallboardView)}>
            <TabsContent value="company" className="space-y-4 pt-0">
              <div
                className="min-w-0"
                role="region"
                aria-label="Company wallboard cards"
              >
                {hasVisibleCompanies ? (
                  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                    {visibleCompanies.map(renderCompanyCard)}
                  </div>
                ) : (
                  <EmptyState
                    icon={Tv}
                    title="Nobody in this status"
                    description={`No member is ${statusFilter === 'all' ? 'listed' : filterLabel(statusFilter).toLowerCase()} right now.`}
                  />
                )}
              </div>
            </TabsContent>

            <TabsContent value="person" className="space-y-6 pt-0">
              <Card className="overflow-hidden rounded-[28px] border-stone-200 bg-white shadow-[0_22px_60px_-34px_rgba(15,23,42,0.24)] dark:border-gray-700 dark:bg-gray-800" contentClassName="p-0">
                <div className="flex flex-wrap gap-2 border-b border-stone-200 p-4 dark:border-gray-700">
                  {PERSON_FILTERS.map((filter) => {
                    const selected = statusFilter === filter.value
                    const count =
                      filter.value === 'clocked_in'
                        ? clockedInPeople.length
                        : people.filter((row) => rowMatchesPersonFilter(row, filter.value)).length
                    return (
                      <button
                        key={filter.value}
                        type="button"
                        onClick={() => setStatusFilter(filter.value)}
                        className={cn(
                          'inline-flex min-h-8 items-center gap-2 rounded-lg px-3 text-sm font-medium transition',
                          selected
                            ? 'bg-primary/10 text-primary'
                            : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700/50',
                        )}
                      >
                        <span>{filter.label}</span>
                        <span
                          className={cn(
                            'rounded-full px-1.5 py-0.5 text-[11px]',
                            selected
                              ? 'bg-primary/10 text-primary'
                              : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
                          )}
                        >
                          {count}
                        </span>
                      </button>
                    )
                  })}
                </div>
                <div className="hidden grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_150px_210px] gap-4 border-b border-stone-200 px-6 py-4 text-xs font-semibold uppercase tracking-[0.16em] text-gray-500 dark:border-gray-700 dark:text-gray-400 lg:grid">
                  <span>Person</span>
                  <span>Company</span>
                  <span>Status</span>
                  <span className="text-right">Times</span>
                </div>
                {filteredPeople.length > 0 ? filteredPeople.map((row) => (
                  <div
                    key={row.user.id}
                    className="grid gap-3 border-b border-stone-200 px-6 py-4 transition-colors last:border-b-0 hover:bg-stone-50/80 dark:border-gray-700 dark:hover:bg-gray-700/40 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_150px_210px] lg:items-center"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <div
                        className={cn(
                          'flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl text-xs font-semibold',
                          getMemberStatus(row) === 'no_show'
                            ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-200'
                            : row.presenceStatus === 'online'
                              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200'
                              : row.presenceStatus === 'idle'
                                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-200'
                                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-100',
                        )}
                      >
                        {getInitials(row.user.fullName)}
                      </div>
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-gray-950 dark:text-gray-100">
                          {row.user.fullName}
                        </p>
                        <p className="truncate text-sm text-gray-500 dark:text-gray-400">
                          {row.user.employeeId} · {row.user.username}
                        </p>
                      </div>
                    </div>

                    <p className="truncate text-sm font-medium text-gray-700 dark:text-gray-100">
                      {row.company.name ?? 'Unassigned'}
                    </p>

                    <div className="flex flex-wrap gap-2">
                      {getMemberStatus(row) === 'no_show' ? (
                        <Badge variant="danger">
                          No show{row.shiftStartAt ? ` · due ${formatMaybeTime(row.shiftStartAt)}` : ''}
                        </Badge>
                      ) : null}
                      <Badge variant={PRESENCE_BADGE_VARIANT[row.presenceStatus]}>
                        {PRESENCE_STATUS_LABEL[row.presenceStatus]}
                      </Badge>
                      <Badge variant={ATTENDANCE_BADGE_VARIANT[row.attendanceStatus]}>
                        {ATTENDANCE_STATUS_LABEL[row.attendanceStatus]}
                      </Badge>
                      {row.presenceStatus === 'idle' ? (
                        <Badge variant="warning">
                          Idle {formatElapsedSince(row.lastActivityAt, board?.serverTime)}
                        </Badge>
                      ) : null}
                    </div>

                    <div className="grid grid-cols-3 gap-2 text-xs text-gray-500 dark:text-gray-400 lg:text-right">
                      <div>
                        <p>Login</p>
                        <p className="font-semibold text-gray-800 dark:text-gray-100">{formatMaybeTime(row.loginAt)}</p>
                      </div>
                      <div>
                        <p>Clock</p>
                        <p className="font-semibold text-gray-800 dark:text-gray-100">{formatMaybeTime(row.clockInAt)}</p>
                      </div>
                      <div>
                        <p>Last</p>
                        <p className="font-semibold text-gray-800 dark:text-gray-100">
                          {formatMaybeTime(row.lastActivityAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                )) : (
                  <div className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                    No people match this status.
                  </div>
                )}
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </PageShell>
  )
}

WallboardPage.displayName = 'WallboardPage'
